// A persistent, opaque per-device id (random hex). Identifies "this browser/device" to the new-device
// challenge (0015) — NOT secret-derived and grants nothing on its own. Pluggable store, same pattern as
// cache.ts/secretStore.ts: localStorage by default (web), AsyncStorage/keychain-adjacent storage on React
// Native, memory for tests. Where no storage is available (SSR / privacy mode) the id is undefined, which
// simply opts the client out of the challenge.

const DEVICE_ID_KEY = 'wiser.device_id';

export interface DeviceIdStore {
  get(key: string): Promise<string | null> | string | null;
  set(key: string, val: string): Promise<void> | void;
}

function localStorageDeviceIdStore(): DeviceIdStore {
  return {
    get(k) {
      try { return typeof localStorage === 'undefined' ? null : localStorage.getItem(k); } catch { return null; }
    },
    set(k, v) {
      try { if (typeof localStorage !== 'undefined') localStorage.setItem(k, v); } catch { /* private mode */ }
    },
  };
}

/** In-memory store for tests / SSR. */
export function memoryDeviceIdStore(): DeviceIdStore {
  const m = new Map<string, string>();
  return {
    get(k) { return m.get(k) ?? null; },
    set(k, v) { m.set(k, v); },
  };
}

let store: DeviceIdStore = localStorageDeviceIdStore();
/** Swap the backend (React Native, tests). Call once at startup, before any sign-in. */
export function setDeviceIdStore(s: DeviceIdStore): void { store = s; }

export async function getOrCreateDeviceId(): Promise<string | undefined> {
  try {
    let id = await store.get(DEVICE_ID_KEY);
    if (!id) {
      const b = new Uint8Array(16);
      crypto.getRandomValues(b);
      id = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
      await store.set(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}
